import * as Sentry from '@sentry/browser';
import { fetchRequest } from 'Utils/api-fetch-request';
import { VuMediChatbotInline } from 'Chatbot';
import { VaiaProductTour } from 'Utils/product-tours';
import { UserStore } from 'Main/user-store';
import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';

import { BrightcovePlayer } from './brightcove-player';
import { WebBrandAlertsClient } from './brand-alerts-client';
import BrightcovePollsClient from './polls-client';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

const learningJourneyShowMore = () => {
    const showMoreButtons = Array.from(document.querySelectorAll('.js_relatedGroupButton'));

    if (!showMoreButtons.length) return;

    showMoreButtons.forEach((button) => {
        if (button.dataset.initialized === 'true') return;

        button.dataset.initialized = 'true';
        button.addEventListener('click', () => {
            button.closest('.js_relatedContainerGroup').classList.add('is-expanded');
            button.remove();
        });
    });
};

const relatedContentPathSelector = async () => {
    const tabsWrap = document.querySelector('.js_relatedContainerTabs');

    if (!tabsWrap) return;

    const tabs = Array.from(tabsWrap.querySelectorAll('.js_relatedContainerTabsItem'));
    const activeContentElement = document.querySelector('.js_relatedContainerContent.is-active');
    const parentContainerElement = activeContentElement.parentElement;
    const contentElementClasses = activeContentElement.className;
    const autoplayButton = document.querySelector('.js_autoplayButton');

    const tabClickHandler = (tab, tabId) => {
        // Switch between different tabs and content elements
        const viewportWidth = window.innerWidth;
        document.querySelector('.js_relatedContainerContent.is-active').classList.remove('is-active');
        document.querySelector('.js_relatedContainerTabsItem.is-active').classList.remove('is-active');
        document.getElementById(`${tabId}-tab-content`).classList.add('is-active');
        tab.classList.add('is-active');

        const activeTooltipElement = document.querySelector('[data-toggle="tooltip"]:hover');
        if (activeTooltipElement) {
            if (viewportWidth >= 1024) {
                tab.addEventListener('mouseleave', () => {
                    $(activeTooltipElement).tooltip('hide');
                });
            } else {
                setTimeout(() => {
                    $(activeTooltipElement).tooltip('hide');
                }, 3000);
            }
        }
    };

    for (const [index, tab] of tabs.entries()) {
        const { tabUrl, tabId } = tab.dataset;
        const existingContentElement = document.querySelector(`.js_relatedContainerContent#${tabId}-tab-content`);

        if (existingContentElement) {
            // if the tab content elements already exists for this tab, just attach the event handler
            tab.addEventListener('click', () => {
                tabClickHandler(tab, tabId);
            });
        } else {
            // fetch content for each of the tabs and attach the event handler
            const newContentElement = document.createElement('div');
            newContentElement.className = contentElementClasses.replace('is-active', '').trim();
            newContentElement.id = `${tabId}-tab-content`;
            parentContainerElement.insertBefore(newContentElement, autoplayButton);

            try {
                const response = await fetch(tabUrl);
                if (!response.ok) {
                    throw new Error(`A response error occurred: ${response.statusText}`);
                }

                const data = await response.text();
                newContentElement.innerHTML = data || `<p>There are no videos to display</p>`;
            } catch {
                newContentElement.innerHTML = `<p>An error occurred while fetching videos. Please try again later.</p>`;
            } finally {
                tab.addEventListener('click', () => {
                    tabClickHandler(tab, tabId);
                });

                if (index === tabs.length - 1) {
                    // run the function only once after the last batch of data is collected
                    learningJourneyShowMore();
                }
            }
        }
    }
};

const relatedItemEventTracking = () => {
    const relatedContainer = document.querySelector('.js_relatedContainer');

    if (!relatedContainer) return;

    const {
        fsAbTestName: abTestName,
        fsAbTestVariant: abTestVariant,
        fsMaxLsIndex: maxLsIndex,
    } = relatedContainer.dataset;

    relatedContainer.addEventListener('click', (event) => {
        const item = event.target.closest('.js_relatedVideoCard');

        if (item && item.dataset) {
            const {
                fsRelatedItemIndex: itemIndex,
                fsPathSelectorTabGroup: pathSelector,
                fsItemInLectureSeries: isLsContent,
                fsLearningJourneyGroup: learningJourneyGroup,
                fsLearningJourneyItemIndex: learningJourneyItemIndex,
            } = item.dataset;
            const tags = item.querySelector('.js_relatedVideoCardTagsWrap')?.dataset.tags || [];

            window.eventTracker.trackRelatedContentClick({
                itemIndex,
                tags,
                pathSelector,
                abTestName,
                abTestVariant,
                isLsContent,
                maxLsIndex,
                learningJourneyGroup,
                learningJourneyItemIndex,
            });
        }
    });
};

const jumpToRelatedContent = () => {
    const button = document.querySelector('.js_jumpToRelatedButton');
    const relatedContainer = document.querySelector('.js_relatedContainer');

    const buttonClickHandler = () => {
        relatedContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    if (!button || !relatedContainer) return;

    button.addEventListener('click', () => buttonClickHandler());
};

const collectHomefeedData = () => {
    const search = new URLSearchParams(window.location.href.split('/').pop());
    const idx = parseInt(search.get('idx'), 10);

    if (search.get('source') === 'homefeed' && Number.isInteger(idx)) {
        window.eventTracker.trackHomefeedRecommended(context.videoId, idx, search.has('campaign'));
    }
};

/**
 * Show AI assistant tooltip, product tour.
 *
 * If forced = true, a smaller "forced" version of the tooltip is displayed. This smaller version is
 * used to display AI features to users that have already seen the whole product tour.
 */
const showProductTour = (forced = false) => {
    const tour = VaiaProductTour(forced);

    window.setTimeout(() => {
        const handleTourDone = () => {
            trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_CLICKED);
            UserStore.saveVAIATooltipSeen(true);
        };

        tour.on('cancel', handleTourDone);
        tour.on('complete', handleTourDone);

        tour.start();
    }, 500);
};

const setupChatbot = async (brightcovePlayer) => {
    const container = document.querySelector('.js_vmChatbotInline');
    const mobileTriggerContainer = document.querySelector('.js_vmChatbotInlineMobileTrigger');

    if (!container || !mobileTriggerContainer) {
        return;
    }

    // Determine whether the client app wants to show the tooltip
    const tooltipSeen = UserStore.loadVAIATooltipSeen();
    const tooltipForced = new URL(window.location.href).searchParams.get('ai-summary') !== null;
    const showTooltip = !tooltipSeen || tooltipForced;

    const chatbot = new VuMediChatbotInline(
        parseInt(context.videoId, 10),
        context.viewedUUID,
        brightcovePlayer,
        context.videoSlug,
        showTooltip,
    );

    const initialized = await chatbot.initialize(container, mobileTriggerContainer);

    if (initialized && chatbot.showProductTour) {
        // If a tooltip has been seen by the user, force the minimal version
        showProductTour(tooltipSeen);
        return true;
    }

    return false;
};

document.addEventListener('DOMContentLoaded', () => {
    const brightcovePlayer = new BrightcovePlayer();

    brightcovePlayer
        .initialize(
            document.getElementById(context.playerTagId),
            context.brightcoveId,
            context.accountId,
            context.playerId,
        )
        .then(async (player) => {
            const willShowProductTour = await setupChatbot(brightcovePlayer);

            if (context.viewedUUID) {
                new WebBrandAlertsClient(player).start(willShowProductTour ? 'chatbot_active_dismiss' : false);
                new BrightcovePollsClient(player).start();
            }
        });

    // Autoplay
    const toggleAutoplay = document.getElementById(context.toggleAutoplayId);

    toggleAutoplay?.addEventListener('click', (e) => {
        const button = e.target;
        let url = context.autoplayURL;

        if (button.innerHTML === 'ON') {
            url = `${url}?autoplay=False`;
            button.innerHTML = 'OFF';
        } else {
            url = `${url}?autoplay=True`;
            button.innerHTML = 'ON';
        }

        fetchRequest(url)
            .then((data) => {
                window.autoplayEnabled = data.result;
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to toggle autoplay:', error);

                Sentry.captureException(error, {
                    tags: {
                        component: `#${context.toggleAutoplayId}`,
                    },
                    extra: {
                        url,
                        buttonState: button?.innerHTML,
                    },
                });
            });
    });

    $('.js_navigationLogInButton').on('click', (event) => {
        event.preventDefault();
        $('#loginOrRegisterModal').modal('show');
    });

    relatedContentPathSelector();
    relatedItemEventTracking();
    jumpToRelatedContent();
    learningJourneyShowMore();
    collectHomefeedData();
});
